// Live prices for the tokens a wallet holds — read from the shared store, and
// fed into it from the tape.
//
// The store is what makes the portfolio and the token page agree: whoever
// learns a price publishes it there, so a token has ONE last-known price in the
// app (and across tabs) instead of one per screen. See `lib/livePriceStore.ts`
// for the 1.2% gap that made this necessary.
//
// The tape half is here because the app-wide `trades` channel already carries
// every trade on the platform — the pair page subscribes to it too
// (`pairWsBootstrap.ts:79`) — so this is one subscription and no new load.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { TradeSummary } from '../api/types'
import { candleTradePrice } from '../lib/candles'
import type { LivePriceMap } from '../lib/livePortfolioValue'
import { getLivePrice, publishLivePrice, subscribeLivePrices } from '../lib/livePriceStore'
import { isTonQuoteToken } from '../lib/tonQuote'
import { useWsSubscription } from './useWsSubscription'

type TradeFrame = TradeSummary & { base_token?: string; pool_addr?: string }

/**
 * @param tokens The masters to watch. Ticks for anything else are still
 *   published to the store — the token the operator has just bought is not in
 *   this list yet, and that tick is the one that fills its cell — but they do
 *   not re-render this consumer.
 */
export function useLiveTokenPrices(tokens: readonly string[]): LivePriceMap {
  const watched = useMemo(() => new Set(tokens), [tokens])
  const watchedRef = useRef(watched)
  watchedRef.current = watched
  const [version, setVersion] = useState(0)

  // Filtering happens HERE and not at the tick, because those are two different
  // questions: what is worth STORING (everything — the store is the app's
  // shared answer, and a token joins this list a moment after its price
  // arrives) versus what is worth RE-RENDERING for. This provider sits above
  // the router, so a notification for a token nobody holds would re-render the
  // pair Rail and Instant Trade along with the portfolio.
  useEffect(
    () =>
      subscribeLivePrices((token) => {
        if (!watchedRef.current.has(token)) return
        setVersion((n) => n + 1)
      }),
    []
  )

  useWsSubscription<TradeFrame>('trades', (msg) => {
    if (msg.event !== 'trade') return
    const trade = msg.data
    const token = trade?.base_token
    if (!token) return
    // The store's price is TON per token. A USD₮ or jetton pool of the same
    // token prices it in its own quote, so its trade is not a tick for it.
    // A frame that names no quote is not guessed to be TON either: this price
    // sizes a Sell Init offer, and the indexer names the quote on every frame.
    if (!isTonQuoteToken(trade.quote_token)) return

    // Spot, not the execution price — the rule the candle already states. A
    // large trade against a thin curve executes far from the pool's price, and
    // a position is worth the pool's price, not someone else's slippage.
    const priceTon = candleTradePrice(trade)
    if (!Number.isFinite(priceTon) || priceTon <= 0) return
    const priceUsd =
      typeof trade.price_usd === 'number' && Number.isFinite(trade.price_usd) && trade.price_usd > 0
        ? trade.price_usd
        : null
    // The trade's own chain time is what the price DESCRIBES; the store stamps
    // when we HEARD it by itself. Keeping the two apart is what stops a canon
    // read, whose clock is the browser's, from looking newer than every tick.
    publishLivePrice(token, { priceTon, priceUsd, chainAt: trade.ts })
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: version is the store's change edge, not a value read here
  return useMemo(() => {
    // Asks for the eight tokens this wallet holds, not for a copy of every
    // token ever traded on the platform — the store holds all of them by
    // design, and snapshotting the whole map on every tick would make the fix
    // for the em-dash pay for itself in allocations.
    const mine: Record<string, NonNullable<ReturnType<typeof getLivePrice>>> = {}
    for (const token of watched) {
      const price = getLivePrice(token)
      if (price) mine[token] = price
    }
    return mine
  }, [watched, version])
}
